import PaginationUI from "../atoms/PaginationUI";
import ColumnLabels from "../molecules/ColumnLabels";
import ColumnPersonInformationAvatar from "../molecules/ColumnPersonInformationAvatar";
import ColumnText from "../molecules/ColumnText";

export type userAssigned = {
  name: string;
  image: string;
};

export type TasksPageProps = {
  name: string;
  user: string;
  typeUser: "lead" | "user";
  date: string;
  assigned: userAssigned;
  tags: string[];
  status: string;
};

export type TableListTasksProps = {
  people: TasksPageProps[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalResults: number;
};

// Helper function to determine status color
const getStatusColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case "pendiente":
    case "pending":
      return "yellow";
    case "atrasada":
    case "overdue":
      return "red";
    default:
      return "green";
  }
};

const TableListTasks: React.FC<TableListTasksProps> = ({
  people,
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  totalResults,
}) => {
  return (
    <div className="md:px-2 sm:px-6 lg:px-8">
      <div className="mt-4 flow-root">
        {/* Table for larger screens */}
        <div className="hidden md:block overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <div className="max-h-[70vh] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-4 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6"
                    >
                      Tarea
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      <span className="text-fuchsia-600">Lead</span>/
                      <span className="text-primary-600">User</span>
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Fecha
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Asignado a
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Etiqueta
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {people?.map((person) => (
                    <tr key={person?.id}>
                      <ColumnText title={person?.name ?? person?.task} />
                      <ColumnText
                        title={person.user}
                        color={
                          person.typeUser === "lead" ? "indigo" : "fuchsia"
                        }
                      />
                      <ColumnText title={person?.date} />
                      <ColumnPersonInformationAvatar
                        id={person?.assigned?.id ?? ""}
                        image={person?.assigned?.image ?? ""}
                        name={person?.assigned?.name ?? ""}
                      />
                      <ColumnLabels labels={person?.tags} />
                      <ColumnLabels
                        labels={[person?.status]}
                        color={getStatusColor(person?.status)}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Card layout for smaller screens */}
        <div className="md:hidden space-y-4">
          {people?.map((person) => (
            <div
              key={person?.name}
              className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
            >
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Tarea:
                  </span>
                  <ColumnText title={person?.name} />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Lead/User:
                  </span>
                  <ColumnText
                    title={person.user}
                    color={person.typeUser === "lead" ? "indigo" : "fuchsia"}
                  />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Fecha:
                  </span>
                  <ColumnText title={person?.date} />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Asignado a:
                  </span>
                  <ColumnPersonInformationAvatar
                    image={person?.assigned?.image ?? ""}
                    name={person?.assigned?.name ?? ""}
                  />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Etiqueta:
                  </span>
                  <ColumnLabels labels={person?.tags} />
                </div>
                <div>
                  <span className="text-sm font-semibold text-gray-900">
                    Estado:
                  </span>
                  <ColumnLabels
                    labels={[person?.status]}
                    color={getStatusColor(person?.status)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <PaginationUI
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          pageSize={pageSize}
          totalResults={totalResults}
        />
      </div>
    </div>
  );
};

export default TableListTasks;
